/**
 * Colonnes de la liste des parcours évaluateurs (portail admin).
 *
 * Un parcours évaluateur = un évaluateur (humain ou modèle) et les notations
 * qu'il produit. Écrites contre la projection de
 * `listerParcoursEvaluateursSuperAdmin`.
 */
import {
  Badge,
  StatutParcoursBadge,
  filtreFacette,
  triOrdonne,
  type ColonneDataTable,
  type FacetteFiltre,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { Bot, User, Wrench } from "lucide-react";
import type { Id } from "@convex/_generated/dataModel";

const ORDRE_STATUT = ["non_commence", "en_cours", "termine"] as const;

const LIBELLES_STATUT: Record<string, string> = {
  non_commence: "Non commencé",
  en_cours: "En cours",
  termine: "Terminé",
};

const LIBELLES_ACTEUR: Record<string, string> = {
  humain: "Humain",
  ia: "IA",
  system: "Système",
};

export const FACETTES_EVALUATEURS: FacetteFiltre[] = [
  {
    colonne: "acteur",
    libelle: "Type d'évaluateur",
    libelles: LIBELLES_ACTEUR,
    ordre: ["humain", "ia", "system"],
  },
  {
    colonne: "statut",
    libelle: "Statut",
    libelles: LIBELLES_STATUT,
    ordre: ORDRE_STATUT,
  },
];

/** Projection retournée par `listerParcoursEvaluateursSuperAdmin`. */
export type EvaluateurLigne = {
  _id: Id<"parcoursEvaluateur">;
  acteur: { kind: string; model?: string };
  nomEvaluateur?: string;
  prenomEvaluateur?: string;
  emailEvaluateur?: string;
  anonyme?: boolean;
  lienEnvoye?: boolean;
  dateCreation: number;
  titre?: string;
  statutCache?: string;
  organisationNom: string | null;
  campagneNom: string | null;
  nbNotations: number;
  nbNotationsPlus: boolean;
};

/** Nom affichable d'un évaluateur, anonymat respecté. */
function libelleEvaluateur(e: EvaluateurLigne): string {
  if (e.acteur.kind === "ia") return e.acteur.model ?? "Modèle";
  if (e.acteur.kind === "system") return "Système";
  if (e.anonyme) return "Évaluateur anonyme";
  const nom = [e.prenomEvaluateur, e.nomEvaluateur].filter(Boolean).join(" ");
  return nom || e.emailEvaluateur || "Évaluateur";
}

function IconeActeur({ kind }: { kind: string }) {
  if (kind === "ia") return <Bot className="h-4 w-4 text-muted-foreground" />;
  if (kind === "system")
    return <Wrench className="h-4 w-4 text-muted-foreground" />;
  return <User className="h-4 w-4 text-muted-foreground" />;
}

export function colonnesEvaluateurs(): ColonneDataTable<EvaluateurLigne>[] {
  return [
    {
      id: "evaluateur",
      header: "Évaluateur",
      accessorFn: (e) => libelleEvaluateur(e),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-0">
          <IconeActeur kind={row.original.acteur.kind} />
          <div className="min-w-0">
            <div className="font-medium truncate">
              {libelleEvaluateur(row.original)}
            </div>
            {row.original.acteur.kind === "humain" &&
              !row.original.anonyme &&
              row.original.emailEvaluateur && (
                <div className="text-sm text-muted-foreground truncate">
                  {row.original.emailEvaluateur}
                </div>
              )}
          </div>
        </div>
      ),
      enableHiding: false,
      meta: { libelle: "Évaluateur", largeur: "30%" },
    },
    {
      id: "acteur",
      header: "Type",
      accessorFn: (e) => e.acteur.kind,
      filterFn: filtreFacette<EvaluateurLigne>(),
      cell: ({ row }) => (
        <Badge variant="outline">
          {LIBELLES_ACTEUR[row.original.acteur.kind] ??
            row.original.acteur.kind}
        </Badge>
      ),
      meta: { libelle: "Type", largeur: "12%" },
    },
    {
      id: "titre",
      header: "Parcours",
      accessorFn: (e) => e.titre ?? "",
      cell: ({ row }) =>
        row.original.titre ? (
          <span className="text-sm truncate block">{row.original.titre}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
      meta: { libelle: "Parcours", largeur: "24%" },
    },
    {
      id: "nbNotations",
      header: "Notations",
      accessorFn: (e) => e.nbNotations,
      cell: ({ row }) => (
        <span className="data">
          {row.original.nbNotations}
          {row.original.nbNotationsPlus ? "+" : ""}
        </span>
      ),
      meta: { libelle: "Notations", largeur: "10%" },
    },
    {
      id: "statut",
      header: "Statut",
      accessorFn: (e) => e.statutCache ?? "non_commence",
      filterFn: filtreFacette<EvaluateurLigne>(),
      sortFn: triOrdonne<EvaluateurLigne>(ORDRE_STATUT),
      cell: ({ row }) => (
        <StatutParcoursBadge statut={row.original.statutCache} />
      ),
      meta: { libelle: "Statut", largeur: "12%" },
    },
    {
      id: "dateCreation",
      header: "Créé le",
      accessorFn: (e) => e.dateCreation,
      cell: ({ row }) => (
        <span className="data text-sm">
          {dateCourte(row.original.dateCreation)}
        </span>
      ),
      meta: { libelle: "Créé le", largeur: "12%" },
    },
  ];
}
